import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import NotificationsIcon from '@material-ui/icons/Notifications';

import NotificationsModal from 'ui/components/NotificationsModal/NotificationsModal';
import { getUnreadedNotificationsCount } from 'utils/helpers';
import { NotificationTypeEnum } from 'utils/types.d';
import StyledBell from './Bell.styles';
import sound from '../../../../static/notificationSound.mp3';

type Props = {
  notificationsList: any[];
  isVisibleCounter?: boolean;
  bellSize?: number;
  bellId?: string;
  counterVisibleTypes?: NotificationTypeEnum[];
}

interface BellClickEventDetail {
  isCurrent: boolean;
  bellId?: string;
}

const Bell: React.FC<Props> = ({ 
  isVisibleCounter = true, 
  notificationsList = [], 
  bellSize,
  bellId = 'default-bell',
  counterVisibleTypes,
}) => {
  const isInitial = useRef<boolean>(true);
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef(null);

  const openNotificationsModal = (event: React.MouseEvent) => {
    event.stopPropagation();
    
    const bellClickEvent = new CustomEvent<BellClickEventDetail>('bell-click', {
      detail: {
        isCurrent: true,
        bellId,
      },
      bubbles: true,
      composed: true,
    });

    window.dispatchEvent(bellClickEvent);
  };

  useEffect(() => {
    const handleCustomEvent = (event: CustomEvent<BellClickEventDetail>) => {
      const { isCurrent, bellId: clickedBellId } = event.detail;

      if (isCurrent && clickedBellId === bellId) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    };

    const handleClickOutside = () => {
      setIsOpen(false);
    };

    window.addEventListener('bell-click', handleCustomEvent as EventListener);
    document.addEventListener('click', handleClickOutside);

    return () => {
      window.removeEventListener('bell-click', handleCustomEvent as EventListener);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [bellId]);

  const closeNotificationsModal = () => {
    setIsOpen(false);
  };

  const unreadedNotifications = getUnreadedNotificationsCount(notificationsList, counterVisibleTypes);
  const unreadedCount = unreadedNotifications.length;
  const notificationsCounter = unreadedCount > 99 ? '99+' : unreadedCount;
  const isCounterVisible = Boolean(isVisibleCounter && unreadedCount);
  const audio = useMemo(() => new Audio(sound), []);

  useEffect(() => {
    if (unreadedCount === 0) {
      document.title = 'HR Network';
      return;
    }

    document.title = `(${unreadedCount}) HR Network`;
    if (!isInitial.current) {
      audio.play();
    }
    isInitial.current = false;
  }, [audio, unreadedCount]);

  return (
    <>
      <StyledBell
        onClick={openNotificationsModal}
        ref={anchorRef}
        isCounterVisible={isCounterVisible}
        size={bellSize}
        className={isOpen ? 'bell-active' : ''}
      >
        <NotificationsIcon />
        <div className="notifications-counter">{notificationsCounter}</div>
      </StyledBell>
      <NotificationsModal
        ref={anchorRef}
        isOpen={isOpen}
        handleClose={closeNotificationsModal}
        notificationsList={notificationsList}
      />
    </>
  );
};

export default memo(Bell);
